import React, { useEffect, useState, useCallback } from 'react';
import AxiosInstance from '../utils/Axios.ts';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Grid2,
    Card,
    CardContent,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import LocationInput from '../components/objects/LocationInput.tsx';
import DynamicTab from '../components/extras/DynamicTab.tsx'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface PierDayReport {
    id: number;
    parent: number;
    date: string;
    employee_name: string | null;
    pier_name: string | null;
    agent_name: string | null;
    ship_name: string | null;
    arrival_time: string | null;
    weather: string | null;
    wind: string | null;
    agent_staff: string | null;
    check_in: string | null;
    delay: boolean;
    delay_reason: string | null;
    complaint: boolean;
    complaint_reason: string | null;
    compliment: boolean;
    compliment_reason: string | null;
    incident: boolean;
    incident_reason: string | null;
    comment: string;
}

const ReportPage: React.FC = () => {
    const [reports, setReports] = useState<PierDayReport[]>([]);
    const [summaryData, setSummaryData] = useState<any>(null);
    interface Location {
        id: number;
        name: string;
    }

    const [pier, setPier] = useState<Location | null>(null);
    const [date, setDate] = useState<Date | null>(null);
    const [ship_name, setShipName] = useState<string>('');
    const [debouncedShipName, setDebouncedShipName] = useState<string>('');
    const [complaint, setComplaint] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<string>('weather');
    const [selectedReport, setSelectedReport] = useState<PierDayReport | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

    // Debounce ship name input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedShipName(ship_name);
        }, 500); // Wait 500ms after user stops typing

        return () => clearTimeout(timer);
    }, [ship_name]);

    const fetchSummaryData = useCallback(() => {
        const params = new URLSearchParams();
        if (pier) params.append('pier', pier.id.toString());
        if (date) params.append('date', date.toISOString().split('T')[0]);
        if (debouncedShipName) params.append('ship_name', debouncedShipName);
        if (complaint) params.append('complaint', complaint.toString());

        AxiosInstance.get(`pier_report/summary/?${params}`)
            .then((response) => {
                setSummaryData(response.data);
            })
            .catch((error) => console.error('Error fetching summary data:', error));
    }, [pier, date, debouncedShipName, complaint]);

    const fetchTableData = useCallback(() => {
        const params = new URLSearchParams();
        if (date) params.append('date', date.toISOString().split('T')[0]);
        if (pier) params.append('pier', pier.id.toString());
        if (debouncedShipName) params.append('ship_name', debouncedShipName);
        if (complaint) params.append('complaint', complaint.toString());
        
        AxiosInstance.get(`pier_report/?${params}`)
            .then((response) => {
                setReports(response.data.results);
            })
            .catch((error) => console.error('Error fetching table data:', error))
    }, [pier, date, debouncedShipName, complaint]);

    useEffect(() => {
        fetchSummaryData();
        fetchTableData();
    }, [fetchSummaryData, fetchTableData]);

    const getChartData = () => {
        const categoryData = summaryData ? summaryData[selectedCategory] || {} : {};
        return {
            labels: Object.keys(categoryData),
            datasets: [
                {
                    label: `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Breakdown`,
                    data: Object.values(categoryData),
                    backgroundColor: ['#3f51b5', '#ff5722', '#4caf50', '#9c27b0', '#ff9800'],
                },
            ],
        };
    };

    const handleRowClick = (report: PierDayReport) => {
        setSelectedReport(report);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedReport(null);
        setIsDialogOpen(false);
    };

    return (
        <DynamicTab title="IT Master" favicon="/logos/IT logo.png">
        <Box sx={{ padding: 2 }}>
            <Typography variant="h4" gutterBottom>
                Pier Day Reports
            </Typography>
            <Grid2 container spacing={2} alignItems="center">
                {/* Filters */}
                <Grid2 size={2}>
                    <LocationInput
                        value={pier ? pier.id : 0}
                        onChange={(value) => setPier(value)}
                        label="Pier"
                        />
                </Grid2>
                <Grid2 size={2}>
                <TextField
                    label="Date"
                    type="date"
                    value={date ? date.toISOString().split('T')[0] : ''}
                    onChange={(e) => setDate(e.target.value ? new Date(e.target.value) : null)}
                    fullWidth
                    margin="normal"
                />
                </Grid2>
                <Grid2 size={2}>
                    <TextField
                        label="Ship Name"
                        value={ship_name}
                        onChange={(e) => setShipName(e.target.value)}
                        fullWidth
                        margin="normal"
                    />
                </Grid2>
                <Grid2 size={2}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={complaint}
                                onChange={(e) => setComplaint(e.target.checked)}
                            />
                        }
                        label="Complaint"
                    />
                </Grid2>
                <Grid2 size={2}>
                    <Button variant="contained" onClick={() => { fetchSummaryData(); fetchTableData(); }}>
                        Apply Filters
                    </Button>
                </Grid2>
            </Grid2>

            {/* Summary Cards and Chart */}
            <Grid2 container spacing={2} alignItems="center" sx={{ height: '100%' }}>
                {/* Cards Section */}

                <Grid2 size={3}>
                    <Card sx={{ marginBottom: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total reports</Typography>
                            <Typography variant="body1">{summaryData?.total_reports || 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ marginBottom: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total on time</Typography>
                            <Typography variant="body1">{summaryData?.on_time || 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ marginBottom: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total delays</Typography>
                            <Typography variant="body1">{summaryData?.delays || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                <Grid2 size={3}>
                    <Card sx={{ marginBottom: 2 }}>
                        <CardContent>
                            <Typography variant="h6">On time %</Typography>
                            <Typography variant="body1">{summaryData?.on_time && summaryData?.total_reports ? 
                                ((summaryData.on_time / summaryData.total_reports) * 100).toFixed(1) + '%' : '0%'}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ marginBottom: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total Complaints</Typography>
                            <Typography variant="body1">{summaryData?.complaint || 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ marginBottom: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total Compliments</Typography>
                            <Typography variant="body1">{summaryData?.compliment || 0}</Typography>
                        </CardContent>
                    </Card>
                </Grid2>
                {/* Add more summary cards as needed */}


                {/* Chart Section */}
                <Grid2 size={6} sx={{ display: 'flex', justifyContent: 'center'}} >
                    <Paper sx={{ padding: 2}} >
                        <Box sx={{ display: 'flex', gap: 1, marginBottom: 2 }}  >
                            <Button
                                variant={selectedCategory === 'weather' ? 'contained' : 'outlined'}
                                onClick={() => setSelectedCategory('weather')}
                            >
                                Weather
                            </Button>
                            <Button
                                variant={selectedCategory === 'wind' ? 'contained' : 'outlined'}
                                onClick={() => setSelectedCategory('wind')}
                            >
                                Wind
                            </Button>
                            <Button
                                variant={selectedCategory === 'staff' ? 'contained' : 'outlined'}
                                onClick={() => setSelectedCategory('staff')}
                            >
                                Staff
                            </Button>
                            <Button
                                variant={selectedCategory === 'checkIn' ? 'contained' : 'outlined'}
                                onClick={() => setSelectedCategory('checkIn')}
                            >
                                Check-In
                            </Button>
                        </Box>
                        <Typography variant="h6" sx={{ marginBottom: 2 }}>
                            Chart
                        </Typography>
                            {summaryData && <Bar data={getChartData()} />}
                    </Paper>
                </Grid2>
            </Grid2>





                {/* Table */}
                <Grid2 size={12}>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Pier</TableCell>
                                    <TableCell>Agent</TableCell>
                                    <TableCell>Ship Name</TableCell>
                                    <TableCell>Weather</TableCell>
                                    <TableCell>Wind</TableCell>
                                    <TableCell>Delay</TableCell>
                                    <TableCell>Complaint</TableCell>
                                    <TableCell>Compliment</TableCell>
                                    <TableCell>Incident</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reports.map((report) => (
                                    <TableRow
                                        key={report.id}
                                        hover
                                        onClick={() => handleRowClick(report)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{report.date}</TableCell>
                                        <TableCell>{report.employee_name || 'N/A'}</TableCell>
                                        <TableCell>{report.pier_name || 'N/A'}</TableCell>
                                        <TableCell>{report.agent_name || 'N/A'}</TableCell>
                                        <TableCell>{report.ship_name || 'N/A'}</TableCell>
                                        <TableCell>{report.weather || 'N/A'}</TableCell>
                                        <TableCell>{report.wind || 'N/A'}</TableCell>
                                        <TableCell>{report.delay ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{report.complaint ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{report.compliment ? 'Yes' : 'No'}</TableCell>
                                        <TableCell>{report.incident ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid2>

                {/* Dialog for Report Details */}
                <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="md">
                    <DialogTitle>Report Details</DialogTitle>
                    <DialogContent>
                        {selectedReport && (
                            <Box>
                                <Typography variant="body1">
                                    <strong>Date:</strong> {selectedReport.date}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Pier:</strong> {selectedReport.pier_name || 'N/A'}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Ship Name:</strong> {selectedReport.ship_name || 'N/A'}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Agent:</strong> {selectedReport.agent_name || 'N/A'}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Employee:</strong> {selectedReport.employee_name || 'N/A'}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Weather:</strong> {selectedReport.weather || 'N/A'}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Wind:</strong> {selectedReport.wind || 'N/A'}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>Delay:</strong> {selectedReport.delay ? 'Yes' : 'No'}
                                </Typography>
                                {(selectedReport.delay && (
                                <Typography variant="body1">
                                    <strong>Delay Reason:</strong> {selectedReport.delay_reason || 'N/A'}
                                </Typography>
                                ))}
                                <Typography variant="body1">
                                    <strong>Complaint:</strong> {selectedReport.complaint ? 'Yes' : 'No'}
                                </Typography>
                                {(selectedReport.complaint && (
                                <Typography variant="body1">
                                    <strong>Complaint Reason:</strong> {selectedReport.complaint_reason || 'N/A'}
                                </Typography>
                                ))}
                                <Typography variant="body1">
                                    <strong>Compliment:</strong> {selectedReport.compliment ? 'Yes' : 'No'}
                                </Typography>
                                {(selectedReport.compliment && (
                                <Typography variant="body1">
                                    <strong>Compliment Reason:</strong> {selectedReport.compliment_reason || 'N/A'}
                                </Typography>
                                ))}
                                <Typography variant="body1">
                                    <strong>Incident:</strong> {selectedReport.incident ? 'Yes' : 'No'}
                                </Typography>
                                {(selectedReport.incident && (
                                <Typography variant="body1">
                                    <strong>Incident Reason:</strong> {selectedReport.incident_reason || 'N/A'}
                                </Typography>
                                ))}
                                <Typography variant="body1">
                                    <strong>Comment:</strong> {selectedReport.comment || 'N/A'}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog} variant="contained">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </DynamicTab>
    );
};

export default ReportPage;